import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Shield, Users, Trash2, Eye, Check, X } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, Button, Badge, Modal, toast } from '../../components/common';
import RoleForm from './RoleForm';
import { roleApi } from '../../services/api';
import './Roles.css';

export default function RoleList() {
    const queryClient = useQueryClient();

    // Modal states
    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);

    // Fetch roles from API
    const { data: roles, isLoading } = useQuery({
        queryKey: ['roles'],
        queryFn: async () => {
            const res = await roleApi.list();
            return res.data?.data || [];
        }
    });

    // Fetch all permissions
    const { data: allPermissions } = useQuery({
        queryKey: ['allPermissionsGlobal'],
        queryFn: async () => {
            try {
                const res = await roleApi.getAllPermissions();
                return res.data?.data || { all: [], grouped: {} };
            } catch (e) {
                console.error('Error fetching permissions:', e);
                return { all: [], grouped: {} };
            }
        }
    });

    // Fetch selected role permissions
    const { data: selectedRolePermissions, isLoading: loadingRolePerms } = useQuery({
        queryKey: ['rolePermsView', selectedRole?.id],
        queryFn: async () => {
            if (!selectedRole?.id) return [];
            try {
                const res = await roleApi.getPermissions(selectedRole.id);
                console.log('Role permissions response:', res.data);
                return res.data?.data || [];
            } catch (e) {
                console.error('Error fetching role permissions:', e);
                // Fallback to role.permissions if API fails
                return selectedRole?.permissions || [];
            }
        },
        enabled: !!selectedRole?.id && (showViewModal || showEditForm),
        staleTime: 0, // Always refetch
        refetchOnMount: true
    });

    // Delete role mutation
    const deleteRoleMutation = useMutation({
        mutationFn: (roleId) => roleApi.delete(roleId),
        onSuccess: () => {
            toast.success('Role deleted successfully');
            queryClient.invalidateQueries(['roles']);
        },
        onError: (error) => {
            toast.error(error.response?.data?.error?.message || 'Failed to delete role');
        }
    });

    // Handle double click to view role
    const handleDoubleClick = (role) => {
        setSelectedRole(role);
        setShowViewModal(true);
    };

    // Handle edit button click
    const handleEdit = (e, role) => {
        e.stopPropagation();
        setSelectedRole(role);
        setShowEditForm(true);
    };

    // Handle delete button click
    const handleDelete = (e, role) => {
        e.stopPropagation();
        if (role.isSystemRole) {
            toast.error('Cannot delete system role');
            return;
        }
        if (window.confirm(`Are you sure you want to delete the role "${role.name}"?`)) {
            deleteRoleMutation.mutate(role.id);
        }
    };

    // Group permissions by module
    const getGroupedPermissions = (permissionsList) => {
        if (!permissionsList || !Array.isArray(permissionsList)) return {};

        return permissionsList.reduce((acc, perm) => {
            const module = perm.module || 'general';
            if (!acc[module]) {
                acc[module] = [];
            }
            acc[module].push(perm);
            return acc;
        }, {});
    };

    const rolePermsGrouped = getGroupedPermissions(selectedRolePermissions);

    return (
        <div className="roles-page">
            <PageHeader
                title="Role Management"
                subtitle="Manage user roles and access permissions"
                breadcrumbs={[
                    { label: 'Administration' },
                    { label: 'Roles', path: '/roles' }
                ]}
                actions={
                    <Button icon={Plus} onClick={() => setShowAddForm(true)}>
                        Add Role
                    </Button>
                }
            />

            <Card>
                <div className="roles-container">
                    <div className="roles-hint">
                        <Eye size={16} />
                        <span>Double-click on a role to view details, or click the edit button to modify</span>
                    </div>

                    {isLoading ? (
                        <div className="roles-loading">Loading roles...</div>
                    ) : (
                        <div className="roles-grid">
                            {roles?.map((role) => (
                                <div
                                    key={role.id}
                                    className="role-card"
                                    onDoubleClick={() => handleDoubleClick(role)}
                                >
                                    <div className="role-icon">
                                        <Shield size={24} />
                                    </div>
                                    <div className="role-content">
                                        <div className="role-header">
                                            <h4>{role.name}</h4>
                                            {role.isSystemRole && (
                                                <Badge variant="info" size="sm">System</Badge>
                                            )}
                                        </div>
                                        <p className="role-desc">
                                            {role.description || 'No description'}
                                        </p>
                                        <div className="role-meta">
                                            <span className="meta-item">
                                                <Users size={14} />
                                                {role.userCount || 0} users
                                            </span>
                                            <span className="meta-item">
                                                <Shield size={14} />
                                                {role.permissions?.length || 0} permissions
                                            </span>
                                        </div>
                                    </div>
                                    <div className="role-actions">
                                        <button
                                            className="action-btn edit"
                                            onClick={(e) => handleEdit(e, role)}
                                            title="Edit Role"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        {!role.isSystemRole && (
                                            <button
                                                className="action-btn delete"
                                                onClick={(e) => handleDelete(e, role)}
                                                title="Delete Role"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Card>

            {/* Add New Role Modal */}
            <Modal
                isOpen={showAddForm}
                onClose={() => setShowAddForm(false)}
                title="Add New Role"
                size="md"
            >
                <RoleForm
                    role={null}
                    allPermissions={allPermissions}
                    onSuccess={() => {
                        setShowAddForm(false);
                        queryClient.invalidateQueries(['roles']);
                    }}
                    onCancel={() => setShowAddForm(false)}
                />
            </Modal>

            {/* Edit Role Modal */}
            <Modal
                isOpen={showEditForm}
                onClose={() => {
                    setShowEditForm(false);
                    setSelectedRole(null);
                }}
                title={`Edit Role: ${selectedRole?.name || ''}`}
                size="lg"
            >
                <RoleForm
                    role={selectedRole}
                    allPermissions={allPermissions}
                    rolePermissions={selectedRolePermissions}
                    onSuccess={() => {
                        setShowEditForm(false);
                        setSelectedRole(null);
                        queryClient.invalidateQueries(['roles']);
                    }}
                    onCancel={() => {
                        setShowEditForm(false);
                        setSelectedRole(null);
                    }}
                />
            </Modal>

            {/* View Role Modal (Double Click) */}
            <Modal
                isOpen={showViewModal}
                onClose={() => {
                    setShowViewModal(false);
                    setSelectedRole(null);
                }}
                title={`Role Details: ${selectedRole?.name || ''}`}
                size="lg"
            >
                {selectedRole && (
                    <div style={{ padding: '0.5rem 0' }}>
                        {/* Role Info Section */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{
                                fontSize: '0.9375rem',
                                fontWeight: 700,
                                color: '#1e293b',
                                marginBottom: '1rem',
                                paddingBottom: '0.5rem',
                                borderBottom: '2px solid #e2e8f0'
                            }}>
                                Role Information
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block' }}>NAME</label>
                                    <span style={{ fontSize: '0.9375rem', color: '#1e293b' }}>{selectedRole.name}</span>
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block' }}>TYPE</label>
                                    <span>
                                        {selectedRole.isSystemRole ? (
                                            <Badge variant="info">System Role</Badge>
                                        ) : (
                                            <Badge variant="secondary">Custom Role</Badge>
                                        )}
                                    </span>
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block' }}>DESCRIPTION</label>
                                    <span style={{ fontSize: '0.9375rem', color: '#1e293b' }}>{selectedRole.description || 'No description'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Permissions Section */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontSize: '0.9375rem',
                                fontWeight: 700,
                                color: '#1e293b',
                                marginBottom: '1rem',
                                paddingBottom: '0.5rem',
                                borderBottom: '2px solid #e2e8f0'
                            }}>
                                <Shield size={18} />
                                Assigned Permissions ({selectedRolePermissions?.length || 0})
                            </h4>

                            {loadingRolePerms ? (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: '0.75rem' }}>
                                    Loading permissions...
                                </div>
                            ) : !selectedRolePermissions || selectedRolePermissions.length === 0 ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    padding: '2rem',
                                    background: '#fef2f2',
                                    border: '1px dashed #fecaca',
                                    borderRadius: '0.75rem',
                                    color: '#dc2626'
                                }}>
                                    <X size={24} />
                                    <p style={{ margin: '0.5rem 0 0' }}>No permissions assigned to this role</p>
                                </div>
                            ) : (
                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {Object.entries(rolePermsGrouped).map(([module, perms]) => (
                                        <div key={module} style={{
                                            marginBottom: '0.75rem',
                                            background: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '0.75rem',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '0.5rem 1rem',
                                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                                color: 'white',
                                                fontSize: '0.8125rem',
                                                fontWeight: 700
                                            }}>
                                                <span>{module.toUpperCase()}</span>
                                                <span style={{
                                                    background: 'rgba(255,255,255,0.2)',
                                                    padding: '0.125rem 0.5rem',
                                                    borderRadius: '1rem',
                                                    fontSize: '0.75rem'
                                                }}>{perms.length}</span>
                                            </div>
                                            <div style={{
                                                padding: '0.5rem',
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                                gap: '0.375rem'
                                            }}>
                                                {perms.map(perm => (
                                                    <div key={perm.id} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        padding: '0.5rem 0.75rem',
                                                        background: 'white',
                                                        border: '1px solid #dcfce7',
                                                        borderRadius: '0.5rem'
                                                    }}>
                                                        <Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} />
                                                        <span style={{
                                                            fontSize: '0.75rem',
                                                            fontWeight: 600,
                                                            color: '#1e293b',
                                                            background: '#f0fdf4',
                                                            padding: '0.125rem 0.375rem',
                                                            borderRadius: '0.25rem'
                                                        }}>{perm.code}</span>
                                                        <span style={{
                                                            fontSize: '0.6875rem',
                                                            color: '#64748b',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>{perm.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '0.75rem',
                            paddingTop: '1rem',
                            borderTop: '1px solid #e2e8f0'
                        }}>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowViewModal(false);
                                    setSelectedRole(null);
                                }}
                            >
                                Close
                            </Button>
                            <Button
                                icon={Edit2}
                                onClick={() => {
                                    setShowViewModal(false);
                                    setShowEditForm(true);
                                }}
                            >
                                Edit Role
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
